import { action, computed, makeObservable, observable } from 'mobx';
import { pagesMap } from '../Pages/pagesBooks/pagesBook';
import { AuthStore } from './auth';
import { ProfileStore } from './profile';
import { ICompanyTypeId, IRole, isCompanyTypeIdIsDenied, isCompanyTypeIdIsRight, isRoleRight } from '../settings';
import { AppStatus, AppSystem, IFeatureFlags, IPage, IRoute, IRouterState, IRoutes } from '../interfaces';
import { isCorrectFlag } from '../middleware';
import { rightAppSignalSendMessage } from '../instruments';
import { ITrPage } from '../translate';

const AuthStoreInstance = AuthStore.getInstance();
const ProfileStoreInstance = ProfileStore.getInstance();

const _genPageKey = (ob: IPage): string => ob.componentName + '-' + ob.url;
const _defaultPage: IPage = pagesMap.find(
    (p: IPage) => p.appStatus === AppStatus.INITIALIZATION && p.isActive && p.isDefault,
) || {
    id: 'XXX',
    componentName: 'Initialization',
    title: 'Initialization',
    url: '/',
};

if (_defaultPage.id === 'XXX') {
    rightAppSignalSendMessage({
        action: 'Not found Initialization Component',
        tags: { error: '_defaultPage.id === "XXX"' },
    });
    console.error('Not found Initialization Component');
}

const _defaultComponent: IRouterState = {
    routeName: _defaultPage.componentName,
};

type IFilterAvailablePages = {
    appSystem?: AppSystem;
    companyTypeId: ICompanyTypeId;
    featureFlags: IFeatureFlags;
    isImpersonating: boolean;
    pages: Array<IPage>;
    role: IRole;
    tariffsToBuyPresent: boolean;
    tariffPackagesToBuyPresent: boolean;
};

function filterAvailablePages({
    appSystem,
    companyTypeId,
    featureFlags,
    isImpersonating,
    pages,
    role,
    tariffsToBuyPresent,
    tariffPackagesToBuyPresent,
}: IFilterAvailablePages): Array<IPage> {
    return pages
        .filter((p: IPage) => !!p.isActive)
        .filter((p: IPage) => !appSystem || !p.appSystem || p.appSystem === appSystem)
        .filter((p: IPage) => !(isImpersonating && p.isNotImpersonating))
        .filter((p: IPage) => {
            if (p.roleAndFlagOperator === 'OR') {
                const hasRoleRight = isRoleRight(p.roleRight, role);
                const hasCorrectFlag = isCorrectFlag(p, featureFlags);
                return hasRoleRight || hasCorrectFlag;
            } else {
                return isRoleRight(p.roleRight, role) && isCorrectFlag(p, featureFlags);
            }
        })
        .filter((p: IPage) => !isCompanyTypeIdIsDenied(companyTypeId, p.companyTypeDenied))
        .filter((p: IPage) => isCompanyTypeIdIsRight(companyTypeId, p.companyTypeRight))
        .filter((p: IPage) => {
            const pageHasTariffsToBuy = !!(p as any).tariffsToBuyPresent;
            const pageHasTariffPackagesToBuy = !!(p as any).tariffPackagesToBuyPresent;

            if (pageHasTariffsToBuy || pageHasTariffPackagesToBuy) {
                return tariffsToBuyPresent || tariffPackagesToBuyPresent;
            }

            return true;
        });
}

export class RouterPagesStore {
    private constructor() {
        makeObservable(this);
    }

    private static instance: RouterPagesStore;

    public static getInstance(): RouterPagesStore {
        if (!RouterPagesStore.instance) {
            RouterPagesStore.instance = new RouterPagesStore();
        }
        return RouterPagesStore.instance;
    }

    @computed get appStatus(): AppStatus {
        return AuthStoreInstance.appStatus;
    }

    @computed get availablePages(): IPage[] {
        const _components: IPage[] = [];
        const _keyRoute: Array<string> = [];
        const role = ProfileStoreInstance.profile.role as IRole;
        const tariffsToBuyPresent = !!ProfileStoreInstance.profile.tariffsToBuyPresent;
        const tariffPackagesToBuyPresent = !!ProfileStoreInstance.profile.tariffPackagesToBuyPresent;
        const myCompanyTypeId = ProfileStoreInstance.profile.companyTypeId as ICompanyTypeId;
        const isImpersonating = ProfileStoreInstance.isImpersonating;
        const availablePages = (routes: IPage[]): void => {
            filterAvailablePages({
                companyTypeId: myCompanyTypeId,
                featureFlags: ProfileStoreInstance.profileFeatureFlags as IFeatureFlags,
                isImpersonating,
                pages: routes,
                role: role,
                tariffsToBuyPresent,
                tariffPackagesToBuyPresent,
            })
                .filter((p: IPage) => !p.appSystem || p.appSystem === this.appSystem)
                .filter((p: IPage) => p.appStatus === this.appStatus)
                .forEach((p: IPage) => {
                    if (p.componentName && p.url) {
                        const _newKey = _genPageKey(p);
                        if (_keyRoute.indexOf(_newKey) < 0 || p.isMenuSeparator) {
                            _keyRoute.push(_newKey);
                            _components.push(p);
                        }
                    }
                    if (p.subMenu && Array.isArray(p.subMenu) && p.subMenu.length) {
                        availablePages(p.subMenu);
                    }
                });
        };
        if (pagesMap && Array.isArray(pagesMap) && pagesMap.length) {
            availablePages(pagesMap);
        } else {
            console.error('Need check pages Settings');
        }
        return _components;
    }

    @computed get defaultPage(): IPage {
        const defaultPage = this.availablePages
            .filter((p: IPage) => !p.appSystem || p.appSystem === this.appSystem)
            .find((p: IPage) => p.isDefault);
        if (defaultPage) {
            return defaultPage;
        } else {
            rightAppSignalSendMessage({
                action: 'STORE-> Not found default component ->',
                tags: {
                    appStatus: this.appStatus,
                    pages: JSON.stringify(
                        this.availablePages
                            .filter((p: IPage) => !p.appSystem || p.appSystem === this.appSystem)
                            .map((i) => i.id),
                    ),
                },
            });

            return _defaultPage;
        }
    }
    @computed get defaultComponent(): IRouterState {
        return { routeName: this.defaultPage.componentName };
    }

    @observable requestedPage: IRouterState = { routeName: 'Dashboard' };
    @action setRequestedPage(page: IRouterState): void {
        this.requestedPage = page;
    }

    @observable requestedUrl = '';
    @action setRequestedUrl(url: string): void {
        this.requestedUrl = url;
    }

    @computed get availableComponents(): IRouterState[] {
        return this.availablePages
            .filter((p: IPage) => !p.appSystem || p.appSystem === this.appSystem)
            .map((page: IPage) => ({
                appSystem: page.appSystem,
                routeName: page.componentName,
                forcedUpdate: page.forcedUpdate,
                title: page.title,
            }));
    }

    @observable currentComponent: IRouterState = _defaultComponent;
    @computed get currentPage(): IPage {
        const foundPage = this.availablePages
            .filter((p: IPage) => !p.appSystem || p.appSystem === this.appSystem)
            .find((p: IPage) => p.componentName === this.currentComponent.routeName);

        if (foundPage) {
            return foundPage;
        } else {
            return this.defaultPage;
        }
    }

    @action setCurrentComponent(component: IRouterState): void {
        const foundComponent = this.availableComponents.find((c: IRouterState) => c.routeName === component.routeName);

        if (foundComponent) {
            if (foundComponent.appSystem && foundComponent.appSystem !== this.appSystem) {
                this.setAppSystem(foundComponent.appSystem);
            }
            this.currentComponent = {
                ...component,
                forcedUpdate: foundComponent.forcedUpdate,
                title: foundComponent.title,
            };
        } else {
            if (this.appStatus === AppStatus.READY && !['Initialization', 'Login'].includes(component.routeName)) {
                rightAppSignalSendMessage({
                    action: 'The user is not right for the page!',
                    tags: {
                        pageAppStatus: this.appStatus,
                        pageMoveComponent: this.defaultComponent.routeName,
                        pageRequestComponent: component.routeName,
                        pageRequestUrl: this.requestedUrl,
                    },
                });
            }
            this.currentComponent = this.defaultComponent;
        }
    }

    @computed get allRoutes(): IRoutes {
        const _activeRoutes: IRoutes = [];
        const _genKey = (ob: IRoute): string => ob.name + '-' + ob.pattern;
        const _keyRoute: Array<string> = [];
        const handleRoute = (routes: IPage[]): void => {
            routes
                .filter((r: IPage) => r.isActive)
                .filter((r: IPage) => !r.isMenuSeparator)
                .forEach((r: IPage) => {
                    if (r.componentName && r.url) {
                        const _newRoute = { name: r.componentName, pattern: r.url };
                        const _newKey = _genKey(_newRoute);
                        if (_keyRoute.indexOf(_newKey) < 0) {
                            _keyRoute.push(_newKey);
                            _activeRoutes.push(_newRoute);
                        }
                        if (r.isDefault) {
                            const _newRoute = { name: 'notFound', pattern: r.url };
                            const _newKey = _genKey(_newRoute);
                            if (_keyRoute.indexOf(_newKey) < 0) {
                                _keyRoute.push(_newKey);
                                _activeRoutes.push(_newRoute);
                            }
                        }
                    }
                    if (r.subMenu && Array.isArray(r.subMenu) && r.subMenu.length) {
                        handleRoute(r.subMenu);
                    }
                });
        };
        if (pagesMap && Array.isArray(pagesMap) && pagesMap.length) {
            handleRoute(pagesMap);
        } else {
            console.error('Need check pages Settings');
        }
        return _activeRoutes;
    }

    @computed get menu(): IPage[] {
        if (pagesMap && Array.isArray(pagesMap) && pagesMap.length) {
            return this.handleMenu(pagesMap);
        } else {
            console.error('Need check privatePages Settings');
            return [];
        }
    }

    @observable isUseExternalUrlManager = false;

    @action setUseExternalUrlManager(value: boolean): void {
        this.isUseExternalUrlManager = value;
    }

    @observable isUseExternalPageTitleManager = false;

    @action setUseExternalPageTitleManager(value: boolean): void {
        this.isUseExternalPageTitleManager = value;
    }

    handleMenu = (page: IPage[]): IPage[] => {
        const role = ProfileStoreInstance.profile.role as IRole;
        const myCompanyTypeId = ProfileStoreInstance.profile.companyTypeId as ICompanyTypeId;
        const featureFlags = ProfileStoreInstance.profileFeatureFlags as IFeatureFlags;
        const isImpersonating = ProfileStoreInstance.isImpersonating;
        const tariffsToBuyPresent = !!ProfileStoreInstance.profile.tariffsToBuyPresent;
        const tariffPackagesToBuyPresent = !!ProfileStoreInstance.profile.tariffPackagesToBuyPresent;

        return filterAvailablePages({
            appSystem: this.appSystem,
            companyTypeId: myCompanyTypeId,
            featureFlags,
            isImpersonating,
            pages: page,
            role: role,
            tariffsToBuyPresent,
            tariffPackagesToBuyPresent,
        })
            .filter((p: IPage) => p.id)
            .filter((p: IPage) => p.isMenu)
            .sort((pa, pb) => (pa.menuOrder ? pa.menuOrder : 0) - (pb.menuOrder ? pb.menuOrder : 0))
            .map((p) => ({
                id: p.id,
                name: p.componentName ? p.componentName : p.id,
                subMenu: p.subMenu && Array.isArray(p.subMenu) && p.subMenu.length ? this.handleMenu(p.subMenu) : [],
                title: p.title ? p.title : ((p.componentName ? p.componentName : p.id) as ITrPage),
                menuIcon: p.menuIcon,
                isMenuSeparator: p.isMenuSeparator,
                // ------------ may by Add IMenu type ------------
                url: p.url,
                componentName: p.componentName,
            }));
    };

    // ------------ AppSystem ------------
    @observable appSystem: AppSystem = AppSystem.OCS;
    @action setAppSystem(appSystem: AppSystem): void {
        this.appSystem = appSystem;
    }
}
